// angular
import { ApplicationRef, Injectable, inject } from '@angular/core';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
// services
import { StorageService } from './storage.service';
// env
import { environment } from '@environments/environment';

const MAX_CONNECTION_RETRIES = 5;

@Injectable()
export class SocketFactory {
  private sockets: Map<string, Socket> = new Map();
  private failureCountByNamespace: Map<string, number> = new Map();
  private pausedNamespaces: Set<string> = new Set();

  // dependencies
  private readonly _storageService = inject(StorageService);
  private readonly _appRef = inject(ApplicationRef);
  constructor() {}

  // Whether this namespace is paused after too many connection failures.
  isPaused(namespace: string): boolean {
    return this.pausedNamespaces.has(namespace);
  }

  getSocket(namespace: string): Socket | null {
    if (this.pausedNamespaces.has(namespace)) {
      return null;
    }

    const token = this._storageService.getToken();
    const chat_id = this._storageService.getChatId();
    const member_id = this._storageService.getMemberId();
    const lang = this._storageService.getChatLang();

    if (this.sockets.has(namespace)) {
      return this.sockets.get(namespace)!;
    }

    const config: SocketIoConfig = {
      url: `${environment.baseUrl.socket}${namespace}`,
      options: {
        transports: ['websocket'],
        autoConnect: true,
      } as any,
    };

    if (token) {
      (config.options as any).auth = {
        token,
      };

      (config.options as any).query = {
        lang,
        project: 'wejoner',
      };
    } else if (chat_id) {
      (config.options as any).query = {
        chat_id,
        member_id,
        lang,
        is_guest: true,
        project: 'wejoner',
      };
    }

    const socket = new Socket(config, this._appRef);
    const io = socket.ioSocket;

    io.on('connect', () => {
      this.failureCountByNamespace.set(namespace, 0);
    });

    io.on('connect_error', () => {
      const count = (this.failureCountByNamespace.get(namespace) ?? 0) + 1;
      this.failureCountByNamespace.set(namespace, count);

      if (count >= MAX_CONNECTION_RETRIES) {
        this.pausedNamespaces.add(namespace);
        socket.disconnect();
        this.sockets.delete(namespace);
        console.warn(
          `[SocketFactory] Socket "${namespace}" paused after ${MAX_CONNECTION_RETRIES} failed connection attempts.`
        );
      }
    });

    this.sockets.set(namespace, socket);
    return socket;
  }

  resetNamespace(namespace: string) {
    const socket = this.sockets.get(namespace);
    if (socket) {
      socket.disconnect();
      this.sockets.delete(namespace);
    }
  }

  disconnectAll() {
    this.sockets.forEach(socket => {
      socket.disconnect();
    });
    this.sockets.clear();
    this.failureCountByNamespace.clear();
    this.pausedNamespaces.clear();
  }
}
